import * as logger from "common/logger";
import * as strings from "common/strings";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import * as docGenService from "services/docGenService";
import * as staticDataService from "services/staticDataService";
import * as programPricingStore from "store/programPricing";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as peopleSelectors from "store/selectors/people/peopleSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

export const makeEndorsementLabel = ({ config, endorsement }) => {
  const { title } = endorsement ?? {};
  if (!strings.isString(title)) {
    return "Unknown";
  }
  const displayTitle = title.replaceAll(/[_-]/g, " ").trim();
  const label = config?.items?.[title]?.label ?? endorsement?.originalLabel;
  return label ? `${displayTitle}: ${label}` : displayTitle;
};

const makeExtraInputDefaults = (extraInputs) =>
  Object.fromEntries(
    (extraInputs ?? [])
      .filter((input) => input?.key != null && input?.default != null)
      .map((input) => [input.key, input.default])
  );

export const makeEndorsement = (config) => (title) => {
  const item = config?.items?.[title];
  return {
    originalLabel: item?.label,
    // As-at snapshot of the config sortOrder at the moment this endorsement was
    // created, persisted with the submission. This is PROVENANCE ONLY — it records
    // the default position config implied at the time so we can later tell whether
    // an endorsement's stored position came from config or a manual drag. Do NOT
    // sort by this field: live ordering always reads config.items[title].sortOrder
    // (this stamped value can go stale if config changes after creation).
    sortOrder: item?.sortOrder,
    ...makeExtraInputDefaults(item?.extraInputs),
    title,
  };
};

// Stable sort of endorsements by the globally-unique `sortOrder` defined on
// each endorsement type in config (`config.items[title].sortOrder`).
// Endorsements whose type has no configured sortOrder are kept after the
// ordered ones, preserving their existing relative order.
export const sortEndorsementsByConfigOrder = ({ config, endorsements }) =>
  (endorsements ?? [])
    .map((endorsement, index) => ({ endorsement, index }))
    .sort((a, b) => {
      const orderA = config?.items?.[a.endorsement?.title]?.sortOrder;
      const orderB = config?.items?.[b.endorsement?.title]?.sortOrder;
      const keyA = orderA == null ? Infinity : orderA;
      const keyB = orderB == null ? Infinity : orderB;
      if (keyA !== keyB) {
        return keyA - keyB;
      }
      return a.index - b.index;
    })
    .map(({ endorsement }) => endorsement);

const sortOrderOf = (config, endorsement) =>
  config?.items?.[endorsement?.title]?.sortOrder;

// Indices (into `endorsements`) of the endorsements that are already in sorted
// order — the longest non-decreasing subsequence by sortOrder. Endorsements
// without a sortOrder are excluded entirely. This is the set of endorsements
// considered "in the correct location"; everything else (e.g. items the user
// has manually dragged out of place) is ignored when choosing an insert point.
const sortedBackboneIndices = ({ config, endorsements }) => {
  const sortable = (endorsements ?? [])
    .map((endorsement, index) => ({
      index,
      order: sortOrderOf(config, endorsement),
    }))
    .filter(({ order }) => order != null);

  const n = sortable.length;
  if (n === 0) {
    return [];
  }

  const length = new Array(n).fill(1);
  const previous = new Array(n).fill(-1);
  let best = 0;
  for (let a = 1; a < n; a += 1) {
    for (let b = 0; b < a; b += 1) {
      if (sortable[b].order <= sortable[a].order && length[b] + 1 > length[a]) {
        length[a] = length[b] + 1;
        previous[a] = b;
      }
    }
    if (length[a] > length[best]) {
      best = a;
    }
  }

  const indices = [];
  for (let cur = best; cur !== -1; cur = previous[cur]) {
    indices.unshift(sortable[cur].index);
  }
  return indices;
};

// Inserts a single endorsement into its sorted position WITHOUT re-sorting the
// rest of the list. The insert point is chosen relative to the sorted backbone
// (see sortedBackboneIndices), so endorsements that are out of place are left
// untouched and do not affect where the new endorsement lands. An endorsement
// with no configured sortOrder is appended to the end.
export const insertEndorsementBySortOrder = ({
  config,
  endorsements,
  endorsement,
}) => {
  const list = endorsements ?? [];
  const order = sortOrderOf(config, endorsement);
  if (order == null) {
    return [...list, endorsement];
  }

  let insertAt = list.length;
  for (const index of sortedBackboneIndices({ config, endorsements: list })) {
    if (sortOrderOf(config, list[index]) > order) {
      insertAt = index;
      break;
    }
  }

  return [...list.slice(0, insertAt), endorsement, ...list.slice(insertAt)];
};

export const makeDefaultEndorsements = (config) =>
  config.defaultSelectedEndorsements.map((endorsement) =>
    strings.isString(endorsement)
      ? makeEndorsement(config)(endorsement)
      : {
          ...makeEndorsement(config)(endorsement?.title),
          ...endorsement,
        }
  );

// Reconciles the TRIA-managed forms on a layer's endorsement list so they match
// the set required for the layer's current policy type + TRIA status, WITHOUT
// touching anything else. Only forms listed in `config.triaManagedForms` are ever
// added or removed; every other endorsement (manual additions/removals, ordering,
// coverage endorsements) is preserved. Newly required forms are slotted into
// sorted position when `sortEnabled`, otherwise appended, mirroring manual adds.
//
// The desired set comes from `config.triaFormsByPolicyTypeAndStatus[policyType][triaStatus]`.
// If there is no entry for the current combination the list is returned untouched
// (we never strip TRIA forms just because a status is unmapped). Returns the SAME
// array reference when nothing needs to change so callers can skip a no-op update.
export const reconcileTriaForms = ({
  config,
  endorsements,
  policyType,
  triaStatus,
  sortEnabled,
}) => {
  const list = endorsements ?? [];
  const managed = config?.triaManagedForms;
  const byStatus = config?.triaFormsByPolicyTypeAndStatus;
  if (!Array.isArray(managed) || managed.length === 0 || byStatus == null) {
    return list;
  }

  const desired = byStatus?.[policyType]?.[triaStatus];
  if (!Array.isArray(desired)) {
    return list;
  }

  const managedSet = new Set(managed);
  const desiredSet = new Set(desired);

  const currentManaged = list
    .map((endorsement) => endorsement?.title)
    .filter((title) => managedSet.has(title));
  const currentManagedSet = new Set(currentManaged);

  const toRemove = currentManaged.filter((title) => !desiredSet.has(title));
  const toAdd = desired.filter((title) => !currentManagedSet.has(title));

  if (toRemove.length === 0 && toAdd.length === 0) {
    return list;
  }

  const removeSet = new Set(toRemove);
  const withoutRemoved = list.filter(
    (endorsement) => !removeSet.has(endorsement?.title)
  );

  const additions = toAdd.map(makeEndorsement(config));
  if (sortEnabled) {
    return additions.reduce(
      (acc, endorsement) =>
        insertEndorsementBySortOrder({
          config,
          endorsements: acc,
          endorsement,
        }),
      withoutRemoved
    );
  }
  return [...withoutRemoved, ...additions];
};

const resolvePath = (obj, path) =>
  path
    .split(".")
    .reduce((cur, seg) => (cur == null ? undefined : cur[seg]), obj);

const isNumber = (value) => typeof value === "number" && !Number.isNaN(value);

const applyOperator = (actual, operator, operand) => {
  switch (operator) {
    case "$exists":
      return (actual != null) === Boolean(operand);
    case "$eq":
      return actual === operand;
    case "$ne":
    case "$neq":
      return actual !== operand;
    case "$in":
      return Array.isArray(operand) && operand.includes(actual);
    case "$nin":
    case "$not_in":
      return !(Array.isArray(operand) && operand.includes(actual));
    case "$gt":
      return isNumber(actual) && isNumber(operand) && actual > operand;
    case "$gte":
      return isNumber(actual) && isNumber(operand) && actual >= operand;
    case "$lt":
      return isNumber(actual) && isNumber(operand) && actual < operand;
    case "$lte":
      return isNumber(actual) && isNumber(operand) && actual <= operand;
    default:
      logger.error(`Unknown predicate operator: ${operator}`);
      return false;
  }
};

// Sentinel distinguishing "key/path resolves to no value" from a genuine null.
const UNKNOWN = Symbol("unknown");

// An operand of the form {"$path": "<key>"} is resolved against the same context
// as predicate keys, enabling field-to-field comparisons
// (e.g. { "programPricing.TIV": { "$gt": { "$path": "program.helixLimit" } } }).
// Any other operand is a literal. An unresolvable $path yields UNKNOWN, which
// fails the operator rather than silently comparing against undefined: a typo in
// a {"$path"} operand of a NEGATIVE operator ($ne/$nin) would otherwise be
// satisfied by every layer and hand out the endorsement to everyone.
const resolveOperand = (operand, resolve) => {
  if (
    operand != null &&
    typeof operand === "object" &&
    !Array.isArray(operand) &&
    Object.keys(operand).length === 1 &&
    "$path" in operand
  ) {
    const resolved = resolve == null ? UNKNOWN : resolve(operand.$path);
    if (resolved === UNKNOWN) {
      logger.error(`Unknown predicate $path operand: ${operand.$path}`);
      return UNKNOWN;
    }
    return resolved;
  }
  return operand;
};

// An object value is an operator map ($gt/$eq/$exists/...); all operators must
// pass. Any other value keeps legacy strict-equality semantics. `resolve` (when
// provided) lets an operator's operand reference another state path via {"$path"}.
// An EMPTY operator map is rejected rather than vacuously satisfied: `{}` used to
// be an ordinary value that strict equality never matched, and a predicate that
// silently matches every layer is the worst failure mode here.
const matchValue = (actual, expected, resolve) => {
  if (
    expected == null ||
    typeof expected !== "object" ||
    Array.isArray(expected)
  ) {
    return actual === expected;
  }
  const operators = Object.entries(expected);
  if (operators.length === 0) {
    logger.error("Empty predicate operator map: {} matches nothing");
    return false;
  }
  return operators.every(([operator, operand]) => {
    const resolved = resolveOperand(operand, resolve);
    return resolved !== UNKNOWN && applyOperator(actual, operator, resolved);
  });
};

export const makeLayerPredicate = ({
  layerId,
  layer,
  underwritersConfig,
  primaryUnderwriter,
  programPricing,
  program,
}) => {
  const layerPricing = programPricing?.layers?.[layerId] ?? {};
  const exposedPerils = (layerPricing.exposedPerils ?? [])
    .filter(({ exposed }) => exposed)
    .map(({ peril }) => peril);

  const policyForm = layer?.policyForm ?? null;
  const primaryUnderwriterLocation =
    underwritersConfig?.all?.find(
      (underwriter) =>
        primaryUnderwriter?.id != null &&
        underwriter?.id === primaryUnderwriter.id
    )?.location ?? null;
  const riskCode = programPricing?.riskCode ?? null;
  const hasNwsCoverage = exposedPerils.includes("NWS");

  const isQuotaShare = layer?.shareOfLine != null && layer.shareOfLine !== 100;
  const hasTria = layer?.triaStatus !== "NOT_INCLUDED";

  const computed = {
    policyForm,
    primaryUnderwriterLocation,
    riskCode,
    hasNwsCoverage,
    isQuotaShare,
    hasTria,
  };

  // Map a predicate key (or a {"$path": ...} operand) to its value in the layer's
  // context. Returns the UNKNOWN sentinel for an unrecognised key namespace.
  const resolveReference = (key) => {
    if (key in computed) return computed[key];
    if (key.startsWith("layer.")) return resolvePath(layer, key.slice(6));
    if (key.startsWith("program.")) return resolvePath(program, key.slice(8));
    if (key.startsWith("programPricing."))
      return resolvePath(programPricing, key.slice(15));
    if (key.startsWith("layerPricing."))
      return resolvePath(layerPricing, key.slice(13));
    return UNKNOWN;
  };

  return (filter) =>
    Object.entries(filter ?? {})
      .map(([key, value]) => {
        const actual = resolveReference(key);
        if (actual === UNKNOWN) {
          logger.error(`Unknown policy predicate key: ${key}`);
          return true;
        }
        return matchValue(actual, value, resolveReference);
      })
      .every(Boolean);
};

const useLayerPredicate = ({ layerId }) => {
  const { data: underwritersConfig } = staticDataService.useConfigQuery(
    "underwriters"
  );

  const primaryUnderwriter = useSelector(
    peopleSelectors.selectPreferredUnderwriter
  );
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const layer = layers?.find((layer) => layer?.id === layerId);

  const programPricing = useSelector(programPricingStore.select);
  const program = useSelector(programSelectors.selectProgram);

  return useMemo(
    () =>
      makeLayerPredicate({
        layerId,
        layer,
        underwritersConfig,
        primaryUnderwriter,
        programPricing,
        program,
      }),
    [
      layerId,
      layer,
      underwritersConfig,
      primaryUnderwriter,
      programPricing,
      program,
    ]
  );
};

// Canonical JSON (object keys sorted, recursively) so two structurally-equal
// entries dedupe identically here and in the backend
// (endorsements_support._dedupe_values uses json.dumps(sort_keys=True)).
const canonicalJson = (value) =>
  JSON.stringify(value, (_key, v) =>
    v != null && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.keys(v)
            .sort()
            .map((k) => [k, v[k]])
        )
      : v
  );

// Removes duplicate entries (strings or object-form entries), keeping the first
// occurrence so overlay order is preserved. Equality is STRUCTURAL, so the plain
// title "X" and the object-form entry { title: "X", ... } are different entries
// and both survive -- deliberately, because the object form carries per-layer
// defaults that must not be thrown away. That means a config which spells the
// same endorsement both ways across two concatenating overlays gets it twice, and
// twice on the document, so warn loudly.
export const dedupeValues = (values) => {
  const seen = new Set();
  const titles = new Set();
  return values.filter((value) => {
    const key = canonicalJson(value);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    const title = strings.isString(value) ? value : value?.title;
    if (title != null) {
      if (titles.has(title)) {
        logger.error(
          `Endorsement "${title}" contributed twice by concatenating overlays in two different shapes; both are kept`
        );
      }
      titles.add(title);
    }
    return true;
  });
};

// `predicateMatch` is persisted per layer (state.endorsements.predicateMatchByLayer)
// and is canonically an array of the predicates that produced the layer's default
// selection. Values written before that was the case are a bare predicate object;
// this maps either shape onto the array form so callers can compare like with like.
export const normalisePredicateMatch = (predicateMatch) => {
  if (predicateMatch == null) {
    return null;
  }
  return Array.isArray(predicateMatch) ? predicateMatch : [predicateMatch];
};

// How `*ByLayerPredicate` overlays combine, chosen per config through the top-level
// `predicateResolution` key of the `endorsements` config:
//   "first-match" (default) -- the first matching overlay's value replaces the base
//                              list verbatim and later overlays are ignored. Configs
//                              authored "specific rule, then catch-all" rely on this.
//   "concatenate"           -- every matching overlay contributes; values are
//                              concatenated in config order and deduplicated (first
//                              occurrence wins). Configs authored as independent,
//                              additive gates (e.g. value-gated forms) rely on this.
// In both modes, if ANY overlay matches its result replaces the base list -- even when
// empty, so a matching `value: []` clears the defaults. The backend
// (endorsements_support.resolve_predicates) applies the same rules.
export const PREDICATE_RESOLUTION_FIRST_MATCH = "first-match";
export const PREDICATE_RESOLUTION_CONCATENATE = "concatenate";

const predicateResolutionMode = (rawConfig) => {
  const mode =
    rawConfig?.predicateResolution ?? PREDICATE_RESOLUTION_FIRST_MATCH;
  if (
    mode !== PREDICATE_RESOLUTION_FIRST_MATCH &&
    mode !== PREDICATE_RESOLUTION_CONCATENATE
  ) {
    logger.error(`Unknown predicateResolution mode: ${mode}`);
    return PREDICATE_RESOLUTION_FIRST_MATCH;
  }
  return mode;
};

const resolveOverlays = ({ overlays, isMatchingLayer, mode }) => {
  if (mode !== PREDICATE_RESOLUTION_CONCATENATE) {
    // Short-circuit, like the `find` this replaced: predicates of later overlays
    // are never evaluated, so an unknown key further down a long list does not
    // log on every layer, and Verve's 68 overlays cost one pass at most.
    const first = overlays.find((entry) => isMatchingLayer(entry?.predicate));
    if (first == null) {
      return null;
    }
    // A matching overlay replaces the base list, so an absent or null `value`
    // means "clear it" -- never `undefined`, which makeDefaultEndorsements (and
    // its backend twin) would blow up on.
    return { value: first.value ?? [], predicates: [first?.predicate ?? null] };
  }
  const matched = overlays.filter((entry) => isMatchingLayer(entry?.predicate));
  if (matched.length === 0) {
    return null;
  }
  // Each entry's value may be a list or a scalar; a missing/null value
  // contributes nothing.
  const values = matched.flatMap((entry) => {
    const value = entry?.value ?? [];
    return Array.isArray(value) ? value : [value];
  });
  return {
    value: dedupeValues(values),
    predicates: matched.map((entry) => entry?.predicate ?? null),
  };
};

export const resolvePredicates = ({ rawConfig, isMatchingLayer }) => {
  let config = { ...rawConfig };
  let predicateMatch = null;
  const mode = predicateResolutionMode(rawConfig);
  for (const key of [
    "defaultVisibleEndorsements",
    "defaultSelectedEndorsements",
    "defaultVisibleForms",
    "defaultVisibleTermsAndConditions",
    "defaultSelectedTermsAndConditions",
    "defaultVisibleSubjectivities",
    "defaultSelectedSubjectivities",
  ]) {
    const overlays = rawConfig?.[`${key}ByLayerPredicate`];
    if (overlays != null) {
      const resolved = resolveOverlays({ overlays, isMatchingLayer, mode });
      if (resolved != null) {
        config[key] = resolved.value;
        if (key === "defaultSelectedEndorsements") {
          predicateMatch = resolved.predicates;
        }
      }
      delete config[`${key}ByLayerPredicate`];
    }
  }

  if (rawConfig?.[`overlayByLayerPredicate`] != null) {
    const entry = rawConfig?.[`overlayByLayerPredicate`]?.find?.((entry) =>
      isMatchingLayer(entry?.predicate)
    );
    if (entry != null) {
      config = { ...config, ...entry.value };
      if (predicateMatch == null) {
        predicateMatch = [entry.predicate];
      }
    }
    delete config[`overlayByLayerPredicate`];
  }

  return { config, predicateMatch };
};

export const makeEndorsementTemplateTitleSet = (templates) =>
  new Set(
    (templates ?? [])
      .filter(
        ({ form }) =>
          form === "WORD_JINJA_ENDORSEMENT" || form === "PDF_ENDORSEMENT"
      )
      .map(({ title }) => title)
  );

export const usePolicyConfig = ({ layerId }) => {
  const {
    data: templates,
    isLoading: isLoadingTemplates,
  } = docGenService.useListTemplatesQuery();

  const {
    data: rawConfig,
    isLoading: isLoadingConfig,
  } = staticDataService.useConfigQuery("endorsements");

  const isMatchingLayer = useLayerPredicate({ layerId });

  const templateTitles = makeEndorsementTemplateTitleSet(templates);

  const { config, predicateMatch } = resolvePredicates({
    rawConfig: {
      ...rawConfig,
      items: Object.fromEntries(
        Object.entries(rawConfig?.items ?? {}).map(([key, value]) => [
          key,
          {
            ...value,
            hasTemplate: templateTitles.has(key),
            template: templates?.find(({ title }) => title === key),
          },
        ])
      ),
    },
    isMatchingLayer,
  });

  return useMemo(
    () => ({
      config,
      predicateMatch,
      isLoading: isLoadingTemplates || isLoadingConfig,
    }),
    [config, predicateMatch, isLoadingTemplates, isLoadingConfig]
  );
};
